import { unzipSync, zipSync } from 'fflate';

/**
 * Shrinks a code zip in the browser before it is uploaded.
 *
 * A typical integration zip is 99% build output (packages, bin, obj, .git) that
 * the server throws away after receiving it. Uploading it anyway is what makes a
 * 34 MB zip take minutes from a slow link. This drops those entries first.
 *
 * The rules below are a copy of backend/services/file_processor.py
 * (EXCLUDED_DIRS, EXCLUDED_EXTENSIONS, should_skip_file, _in_excluded_dir and the
 * nested-zip branch of _collect_zip_candidates). Keep the two in step. The
 * browser may only drop what the server is certain to discard, so when in doubt
 * an entry is kept, and any zip that cannot be copied faithfully is uploaded as is.
 */

const EXCLUDED_DIRS = new Set([
  '.git', 'node_modules', 'packages', 'bin', 'obj', '.vs', 'debug', 'release',
  'dist', 'build', '__pycache__', '.idea', 'testresults', '.nuget',
  'artifacts', '.terraform', 'vendor', 'bower_components', 'coverage',
  'tmp', 'temp', 'logs',
]);

// The server's list also names '.min.js', '.min.css' and '.Designer.cs', but it
// compares only the LAST extension, lower-cased, so those three never match and
// such files are kept. They are left out here for the same result.
const EXCLUDED_EXTENSIONS = new Set([
  '.dll', '.exe', '.pdb', '.nupkg', '.cache', '.suo', '.user',
  '.map', '.lock', '.log',
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.ico', '.svg',
  '.woff', '.woff2', '.ttf', '.eot',
  '.zip', '.tar', '.gz', '.rar', '.7z',
  '.mp3', '.mp4', '.avi', '.mov',
  '.pyc', '.pyo', '.class', '.o', '.obj',
]);

const KEPT_WITHOUT_EXTENSION = new Set([
  'makefile', 'dockerfile', 'readme', 'license', 'gitignore', 'dockerignore',
]);

// True when the server would discard this zip entry on its name alone.
const serverSkips = (path) => {
  const parts = path.toLowerCase().replace(/\\/g, '/').split('/');
  // Python's Path(x).name / .suffix: the last real path component, and the text
  // from its last dot — unless that dot is the first or the last character.
  const name = path.split('/').filter((p) => p && p !== '.').pop() || '';
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 && dot < name.length - 1 ? name.slice(dot).toLowerCase() : '';

  // A zip inside the zip is how B1iF package exports arrive: the server opens it
  // unless one of its folders is excluded.
  if (ext === '.zip') return parts.slice(0, -1).some((p) => EXCLUDED_DIRS.has(p));

  if (parts.some((p) => EXCLUDED_DIRS.has(p))) return true;
  if (EXCLUDED_EXTENSIONS.has(ext)) return true;
  return !ext && !KEPT_WITHOUT_EXTENSION.has(name.toLowerCase());
};

// Things fflate does not report, read straight from the zip's own index. Any one
// means the server would read an entry differently from the copy made here: a
// password-protected entry (the server skips it; a copy would carry scrambled
// text), a name with accented characters stored the old, non-UTF-8 way (it would
// come back under a different name), and a name holding a zero byte (the server
// stops reading the name there, so "a.cs\0.dll" is "a.cs" to it).
const cannotBeCopied = (zip) => {
  const u16 = (i) => zip[i] | (zip[i + 1] << 8);
  const u32 = (i) => (u16(i) | (u16(i + 2) << 16)) >>> 0;

  let end = zip.length - 22;
  while (end >= 0 && u32(end) !== 0x06054b50) end -= 1;
  if (end < 0) throw new Error('not a zip');
  let count = u16(end + 8);
  let at = u32(end + 16);
  if (count === 0xffff || at === 0xffffffff) {
    // Very large zips keep the real numbers in a second record.
    const big = u32(end - 12);
    if (u32(big) === 0x06064b50) {
      count = u32(big + 32);
      at = u32(big + 48);
    }
  }

  for (let n = 0; n < count; n += 1) {
    if (u32(at) !== 0x02014b50) throw new Error('unreadable zip index');
    const flags = u16(at + 8);
    const nameEnd = at + 46 + u16(at + 28);
    if (flags & 1) return true;
    const oldStyleName = !(flags & 0x800);
    if (zip.subarray(at + 46, nameEnd).some((c) => c === 0 || (oldStyleName && c > 127))) return true;
    at = nameEnd + u16(at + 30) + u16(at + 32);
  }
  return false;
};

const MB = 1024 * 1024;

/**
 * Slimming runs on the page itself, which stands still while the kept files are
 * unpacked and packed again — about a second for every 15 to 50 MB of them. Up
 * to 20 MB that is always worth it. Beyond that the zip is slimmed only when it
 * takes at least a fifth off the upload, and past 200 MB it is never slimmed.
 *
 * keptBytes: the kept files' size once unpacked (the work to do).
 * skippedBytes: the space the skipped files take in the zip (the upload saved).
 */
export const worthSlimming = (zipBytes, keptBytes, skippedBytes) => (
  keptBytes <= 20 * MB || (keptBytes <= 200 * MB && skippedBytes >= zipBytes / 5)
);

/**
 * Returns { file, originalBytes, slimBytes, kept, skipped, changed }.
 *
 * `file` is a new zip with the ORIGINAL filename holding only the entries the
 * server would keep, or the file that was passed in (changed: false) when it is
 * not a zip, nothing would be skipped, nothing would be left, slimming would hold
 * the page up for too little gain, the result is no smaller, or anything at all
 * goes wrong.
 */
export const slimZip = async (file) => {
  const unchanged = {
    file, originalBytes: file.size, slimBytes: file.size, kept: 0, skipped: 0, changed: false,
  };
  if (!/\.zip$/i.test(file.name)) return unchanged;

  try {
    const original = new Uint8Array(await file.arrayBuffer());
    if (cannotBeCopied(original)) return unchanged;

    let skipped = 0;
    let skippedBytes = 0;
    let keptBytes = 0;
    const keptNames = new Set();
    // First only read the zip's list of entries — nothing is unpacked yet.
    unzipSync(original, {
      filter: ({ name, size, originalSize }) => {
        if (name.endsWith('/')) return false; // a folder — the server ignores these
        if (serverSkips(name)) {
          skipped += 1;
          skippedBytes += size;
          return false;
        }
        // The server reads both copies of a name that appears twice; a re-packed
        // zip could only carry one.
        if (keptNames.has(name)) throw new Error('duplicate entry');
        keptNames.add(name);
        keptBytes += originalSize;
        return false;
      },
    });
    const kept = keptNames.size;
    if (!skipped || !kept) return unchanged;
    if (!worthSlimming(file.size, keptBytes, skippedBytes)) return unchanged;

    // Only the kept entries are unpacked, so this stays quick on a large zip.
    const slim = zipSync(unzipSync(original, { filter: ({ name }) => keptNames.has(name) }));
    if (slim.length >= file.size) return unchanged;
    return {
      file: new File([slim], file.name, { type: file.type, lastModified: file.lastModified }),
      originalBytes: file.size,
      slimBytes: slim.length,
      kept,
      skipped,
      changed: true,
    };
  } catch {
    return unchanged; // corrupt, unusual or too big to read — upload it as it is
  }
};

/**
 * Packs loose files into one zip. Code Docs accepts only zips, so loose code
 * files picked there are wrapped before upload. Two files with the same name are
 * both kept — the later one goes into a numbered folder.
 */
export const zipLooseFiles = async (files, zipName) => {
  const entries = {};
  const used = new Set();
  for (let i = 0; i < files.length; i += 1) {
    const name = used.has(files[i].name) ? `${i + 1}/${files[i].name}` : files[i].name;
    used.add(name);
    entries[name] = new Uint8Array(await files[i].arrayBuffer());
  }
  return new File([zipSync(entries)], zipName, { type: 'application/zip' });
};

// ---------------------------------------------------------------------------
// What the upload pages show: sizes, and how the upload itself is going.
// ---------------------------------------------------------------------------

export const formatBytes = (bytes) => (bytes >= 1024 * 1024
  ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
  : `${Math.round(bytes / 1024) || (bytes ? 1 : 0)} KB`);

/**
 * The job card's "uploading" state, built from axios's upload progress event:
 * percent, how much has been sent, and the time left once it can be estimated.
 */
export const uploadingStatus = ({ loaded = 0, total, estimated }) => {
  if (!total) {
    return { status: 'uploading', progress: 0, message: `Uploading — ${formatBytes(loaded)} sent` };
  }
  if (loaded >= total) {
    return { status: 'uploading', progress: 100, message: 'Upload finished — starting the job...' };
  }
  const timeLeft = !Number.isFinite(estimated)
    ? ''
    : estimated < 60 ? ' · less than a minute left' : ` · about ${Math.ceil(estimated / 60)} min left`;
  return {
    status: 'uploading',
    progress: Math.round((loaded / total) * 100),
    message: `Uploading — ${formatBytes(loaded)} of ${formatBytes(total)} sent${timeLeft}`,
  };
};
